import { ConfigStateService, generatePassword, ListService, PagedResultDto } from '@abp/ng.core';
import {
  Confirmation,
  ConfirmationService,
  getPasswordValidators,
  ToasterService,
} from '@abp/ng.theme.shared';
import { ePropType, EXTENSIONS_IDENTIFIER, FormProp } from '@abp/ng.components/extensible';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, Injector, OnInit, signal } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  GetIdentityUsersInput,
  IdentityRoleService,
  IdentityUserDto,
  IdentityUserService,
  IdentityUserUpdatePasswordInput,
  OrganizationUnitService,
} from '@volo/abp.ng.identity/proxy';
import { finalize } from 'rxjs/operators';
import { eIdentityComponents, ExcelService, UsersComponent } from '@volo/abp.ng.identity';
import { identityTwoFactorBehaviourOptions } from '@volo/abp.ng.identity';
import { StaffService } from '@proxy/servicing-service/staffs/staff.service';

@Component({
    selector: 'app-user',
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: './user.component.html',
    styleUrls: ['./user.component.scss'],
    providers: [
        ListService,
        ExcelService,
        {
            provide: EXTENSIONS_IDENTIFIER,
            useValue: eIdentityComponents.Users,
        },
        {
            provide: UsersComponent,
            useExisting: UserComponent,
        },
    ],
    styles: [
        `
      .mh-35 {
        max-height: 35px;
      }
    `,
    ],
    standalone: false
})
export class UserComponent implements OnInit {
  // Flags below are set from async callbacks; ABP's OnPush layout means the view is
  // only re-checked if it is explicitly marked.
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly excelService = inject(ExcelService);

  readonly data = signal<PagedResultDto<IdentityUserDto>>({ items: [], totalCount: 0 });

  setPasswordForm!: FormGroup;

  selected = {} as IdentityUserDto;
  visiblePermissions = false;
  providerKey = '';
  isCreateModalVisible = false;
  isEditModalVisible = false;
  isSetPasswordModalVisible = false;
  modalBusy = false;
  visibleClaims = false;
  claimSubject = {} as { id: string; type: 'roles' | 'users' };
  filters = {} as GetIdentityUsersInput;
  isLockModalVisible = false;
  isInviteModalVisible = false;
  twoFactor = {
    isModalVisible: false,
    checkboxValue: false,
    isOptional: false,
  };
  lockForm!: FormGroup;

  dateTimePickerProps = {
    defaultValue: new Date(),
    displayName: 'AbpIdentity::DisplayName:LockoutEnd',
    validators: () => [Validators.required],
    name: 'lockoutEnd',
    id: 'lockout-end',
    type: ePropType.DateTime,
  } as Partial<FormProp>;

  constructor(
    public readonly list: ListService<GetIdentityUsersInput>,
    public confirmationService: ConfirmationService,
    public service: IdentityUserService,
    public staffService: StaffService,
    public fb: FormBuilder,
    public toasterService: ToasterService,
    public injector: Injector,
    public configState: ConfigStateService,
    public roleService: IdentityRoleService,
    public organizationUnitService: OrganizationUnitService,
  ) {}

  ngOnInit() {
    const { key } = identityTwoFactorBehaviourOptions[0];
    this.twoFactor.isOptional =
      this.configState.getFeature('Identity.TwoFactor') === key &&
      this.configState.getSetting('Abp.Identity.TwoFactor.Behaviour') === key;

    this.hookToQuery();
    this.setPasswordForm = this.fb.group({
      newPassword: ['', [Validators.required, ...getPasswordValidators(this.injector)]],
    });

    this.lockForm = this.fb.group({
      lockoutEnd: [new Date().toISOString(), [Validators.required]],
    });
  }

  onVisiblePermissionChange = (value: boolean) => {
    this.visiblePermissions = value;
  };

  clearFilters() {
    this.filters = {} as GetIdentityUsersInput;
    this.list.get();
  }

  private hookToQuery() {
    this.list
      .hookToQuery(query => this.service.getList({ ...query, ...this.filters }))
      .subscribe(res => (this.data.set(res)));
  }

  onAdd() {
    this.selected = {} as IdentityUserDto;
    this.isCreateModalVisible = true;
  }

  openInviteModal() {
    this.isInviteModalVisible = true;
    this.cdr.markForCheck();
  }

  exportExcel() {
    this.excelService.exportExcel();
  }

  exportCSV() {
    this.excelService.exportCSV();
  }

  onEdit(id: string) {
    this.service.get(id).subscribe(selectedUser => {
      this.selected = selectedUser;
      this.isEditModalVisible = true;
      this.cdr.markForCheck();
    });
  }

  exportFile() {
    const ids = this.data().items.map(user => user.id).filter((id): id is string => !!id);
    this.staffService
      .credentialsExportFromIds(ids)
      .subscribe({
        next: blob => {
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.style.display = 'none';
          a.href = url;
          a.download = 'file' + '.xls';
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
        },
        error: err => {
          console.error('Error downloading the file', err);
        },
      });
  }

  save(payload: Record<string, unknown>) {
    this.modalBusy = true;
    const { id } = this.selected;

    (id ? this.service.update(id, payload as any) : this.service.create(payload as any)).subscribe({
      next: () => {
        this.list.get();
        this.isCreateModalVisible = false;
        this.isEditModalVisible = false;
        this.cdr.markForCheck();
        this.toasterService.success('IdentityService::UserSaved');
        this.modalBusy = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.modalBusy = false;
        this.cdr.markForCheck();
      },
    });
  }

  delete(id: string, userName: string) {
    this.confirmationService
      .warn('AbpIdentity::UserDeletionConfirmationMessage', 'AbpIdentity::AreYouSure', {
        messageLocalizationParams: [userName],
      })
      .subscribe((status: Confirmation.Status) => {
        if (status === Confirmation.Status.confirm) {
          this.service.delete(id).subscribe(() => this.list.get());
        }
      });
  }

  onManageClaims(id: string) {
    this.claimSubject = {
      id,
      type: 'users',
    };

    this.visibleClaims = true;
  }

  unlock(id: string) {
    this.service.unlock(id).subscribe(() => {
      this.toasterService.success('AbpIdentity::UserUnlocked');
      this.list.get();
    });
  }

  openPermissionsModal(providerKey: string) {
    this.providerKey = providerKey;
    setTimeout(() => {
      this.visiblePermissions = true;
      this.cdr.markForCheck();
    }, 0);
  }

  setPassword() {
    if (this.setPasswordForm.invalid) return;
    if (!this.selected.id) return;

    this.modalBusy = true;
    this.service
      .updatePassword(
        this.selected.id,
        this.setPasswordForm.value as IdentityUserUpdatePasswordInput,
      )
      .pipe(
        finalize(() => {
          this.modalBusy = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe(() => {
        this.isSetPasswordModalVisible = false;
        this.cdr.markForCheck();
        this.selected = {} as IdentityUserDto;
        this.setPasswordForm.reset();
      });
  }

  generatePassword() {
    this.setPasswordForm.get('newPassword')?.setValue(generatePassword());
  }

  lock() {
    const { lockoutEnd } = this.lockForm.value;
    if (!this.selected.id) return;

    this.modalBusy = true;
    this.service
      .lock(this.selected.id, lockoutEnd)
      .pipe(
        finalize(() => {
          this.modalBusy = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe(() => {
        this.isLockModalVisible = false;
        this.cdr.markForCheck();
        this.lockForm.reset({
          lockoutEnd: new Date().toISOString(),
        });
        this.list.get();
      });
  }

  setTwoFactor() {
    if (!this.selected.id) return;

    this.modalBusy = true;
    this.service
      .setTwoFactorEnabled(this.selected.id, this.twoFactor.checkboxValue)
      .pipe(
        finalize(() => {
          this.modalBusy = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe(() => (this.twoFactor.isModalVisible = false));
  }

  get isStaff() {
    return true;
  }
}
